import { LoggerService } from '@angular-ru/cdk/logger';
import { inject, Injectable } from '@angular/core';
import { NavigationBehaviorOptions, Router } from '@angular/router';
import { addBreadcrumb, captureException } from '@sentry/angular';
import { ILink } from 'src/app/api/modules/core/components/abstract/ILink';
import { RELOAD_PAGE, SOCIAL_SHARING_FEATURES } from 'src/app/app-constants';
import { environment } from 'src/environments/environment';

/**
 * Interprets DigitaService {@link ILink} objects and performs the navigation
 * each one describes.
 *
 * Links are never followed natively anywhere in this platform. The server sends
 * a descriptor and this service decides what it means: a router navigation, a
 * new tab, a frame navigation that escapes the embedding iframe, a share popup,
 * a page refresh, or nothing at all.
 */
@Injectable({
  providedIn: 'root',
})
export class LinkService {
  private readonly loggerService = inject(LoggerService);
  private readonly router = inject(Router);

  /**
   * Schemes that are refused outright, whatever the link's type says.
   *
   * A link descriptor names somewhere to go. These three name code to run, so
   * a link carrying one is either a mistake or an attempt — there is no third
   * reading, which is what makes refusing them safe. The reverse rule, an
   * allowlist of known-good schemes, would also refuse the ones nobody here
   * thought of: `whatsapp:`, `sms:`, `intent:` and their kin are real link
   * destinations on mobile, they only work on a device with that app installed,
   * and so a config using one would break somewhere no test would ever see it.
   */
  private static readonly REFUSED_SCHEMES = /^(?:javascript|data|vbscript):/;

  /**
   * Open a DigitaService ILink Object.
   *
   * @param link the ILink Data containing the descriptor of the link
   */
  openLink(link: ILink): void {
    this.loggerService.log('[LinkService]::openLink', link);
    if (link?.href) {
      // Refused before the type is even read. Angular's sanitizer already strips
      // these out of `[href]` bindings, so the anchor a component renders is
      // safe — but `window.location.href = …` and `window.open(…)` below reach
      // the browser directly and would run the code. Checking once here, rather
      // than in each branch that touches the window, means a branch added later
      // cannot forget to.
      if (this.isRefusedScheme(link.href)) {
        return;
      }

      // at this point we know we have a link but don't yet know how to handle it
      switch (link.type) {
        // we will assume if nothing is provided or if the value is wrong, that it is `internal`
        default:
          this.linkRouter(link);
          break;

        // A `none` link deliberately goes nowhere: the component that owns it
        // reacts to the activation itself — copying the href to the clipboard,
        // opening a sheet — so the href is data rather than a destination.
        // Handled here rather than left to `default` because these links carry
        // absolute URLs, and routing to one means resolving a screen from a URL
        // that was never a route. Callers are free to check the type themselves
        // (AbstractLinkComponent does, to raise its own `clicked` event
        // instead), but any that do not now get the documented behavior rather
        // than a navigation.
        case 'none':
          this.loggerService.log('[LinkService]::openLink - type is "none", performing no navigation', link);
          break;

        // if the link is defined as `external-self` we will directly use the window
        case 'external-self':
          this.linkWindowSelf(link);
          break;

        // if the link is defined as `external-blank` we will open tab
        case 'external-blank':
          this.linkWindowBlank(link);
          break;

        // if the link is defined as `external-parent` we will open the link in the parent
        case 'external-parent':
          this.linkWindowParent(link);
          break;

        // if the link is defined as `external-top` we will open the link in the parent
        case 'external-top':
          this.linkWindowTop(link);
          break;

        // if the link is defined as 'social' then it needs significant processing
        case 'social':
          this.linkSocial(link);
          break;
      }
    } else {
      this.loggerService.warn('[LinkService] openLink - No link supplied');
    }
  }

  /**
   * True when the href names code to run rather than somewhere to go.
   *
   * The scheme is read the way a browser reads it, not the way the string
   * looks. Browsers discard ASCII whitespace and control characters while
   * parsing a URL, so a `javascript:` with a tab sitting inside the word, or a
   * space in front of it, both still run; testing the raw string would let
   * either straight through. Everything at or below U+0020 comes out first,
   * which is broader than any browser strips — deliberately, because the cost
   * of over-stripping is nil here. It cannot cause a false refusal: the test is
   * anchored to the start, so an href that merely mentions one of these words
   * further along (inside a query parameter, say) never matches, and no genuine
   * URL begins with these letters split apart by control characters.
   *
   * Filtered by character code rather than by a regex character class. A class
   * spanning control characters is what `no-control-regex` exists to catch, and
   * the rule is right: spelled out, such a range is unreadable and easy to get
   * subtly wrong — this one first went into the file with a raw NUL byte in it.
   *
   * @param href the href being asked for
   */
  private isRefusedScheme(href: string): boolean {
    const asTheBrowserReadsIt = Array.from(href)
      .filter((character) => character.charCodeAt(0) > 0x20)
      .join('')
      .toLowerCase();
    if (!LinkService.REFUSED_SCHEMES.test(asTheBrowserReadsIt)) {
      return false;
    }

    this.loggerService.warn('[LinkService] openLink - refused a link whose scheme names code rather than a destination', href);

    addBreadcrumb({
      category: 'navigation',
      level: 'warning',
      message: 'Link refused: unsafe scheme',
      data: { href },
    });

    return true;
  }

  /**
   * Navigates with the internal Angular Router
   * @param link the ILink data descriptor containing the absolute URL href property
   */
  private linkRouter(link: ILink): void {
    this.loggerService.log('[LinkService]::linkRouter', link);

    // one intent, one navigation (see `isNavigationAlreadyInFlight`)
    if (this.isNavigationAlreadyInFlight(link.href)) {
      return;
    }

    // Typed as the behavior half of NavigationExtras deliberately, because that
    // is all `navigateByUrl` reads. A `queryParamsHandling: 'merge'` used to sit
    // here under a comment promising that identical query params would be
    // merged; that option is consulted only where a URL is CREATED
    // (`router.navigate` / `createUrlTree`), so it never once took effect. This
    // type turns re-adding it into a compile error rather than another comment
    // nobody can disprove. Genuinely merging params across screens would change
    // the URL the resolver sends, which the server acts on — a server-side
    // decision, not a local tidy-up.
    const navigationExtras: NavigationBehaviorOptions = {
      // prevents URL / Address Bar / Location updates
      skipLocationChange: environment.skipLocationChange,
    };

    // navigate to the path
    this.router.navigateByUrl(link.href, navigationExtras);
  }

  /**
   * True when the router is already navigating to this exact target.
   *
   * Every screen change costs a server round trip — the route resolver sends the
   * URL and the server records the engagement before it answers with the screen.
   * A second activation of the same control (a double tap, a stuttered click, an
   * impatient re-press while the first request is still outstanding) used to
   * start a whole second navigation. The router cancels the first one, but the
   * server has already done that work and counted it, so one intent was billed
   * twice.
   *
   * The test is the router's OWN in-flight navigation rather than an
   * elapsed-time window, and that distinction is the point. The gap between an
   * activation and its destination rendering is exactly as long as the request
   * takes, so this protects a slow request for precisely as long as it is slow
   * and stops protecting the moment it settles. A fixed window would have to
   * guess that duration, and would guess wrong in both directions — too short on
   * a bad connection, still swallowing taps on a fast one.
   *
   * It also leaves DELIBERATE re-navigation to the same URL alone. The router
   * runs `onSameUrlNavigation: 'reload'`, so asking for the URL already showing
   * is a real navigation rather than a no-op, and it always follows a rendered
   * destination — nothing is in flight by then, so it passes through untouched.
   * Such a navigation costs no request of its own, because an identical URL
   * changes no route params and the resolver is skipped. Which is the reason the
   * doubled cost only ever came from two activations aimed at a URL other than
   * the current one: both are then a real change of screen, and both resolve.
   *
   * Compared on `extractedUrl`, the target as requested. `finalUrl` is not filled
   * in until redirect recognition has run, so it is still null throughout the
   * resolve — the exact phase this guard covers — and comparing against it would
   * mean the guard never fired at all.
   *
   * Only an identical target is suppressed. A second activation aimed somewhere
   * else is a change of mind, and still cancels and replaces the first.
   *
   * @param href the target of the navigation being requested
   */
  private isNavigationAlreadyInFlight(href: string): boolean {
    const inFlight = this.router.getCurrentNavigation();
    if (!inFlight) {
      return false;
    }

    const target = this.router.serializeUrl(this.router.parseUrl(href));
    if (this.router.serializeUrl(inFlight.extractedUrl) !== target) {
      return false;
    }

    this.loggerService.warn('[LinkService] linkRouter - suppressed a duplicate navigation; this target is already in flight', href);

    // Production silences the logger, so leave a trail that outlives it:
    // breadcrumbs ride along with whichever error is captured next, which is how
    // a "the button did nothing" report gets tied back to a suppression here.
    // A no-op until a Sentry client exists, so local runs are unaffected.
    addBreadcrumb({
      category: 'navigation',
      level: 'info',
      message: 'Duplicate navigation suppressed',
      data: { href, inFlightNavigationId: inFlight.id },
    });

    return true;
  }

  /**
   * Navigates with the window.location property.
   *
   * You can use `reload' as a URL which will refresh the page.
   *
   * @param link the ILink data descriptor containing the absolute URL href property
   */
  private linkWindowSelf(link: ILink): void {
    this.loggerService.log('[LinkService]::linkWindowSelf', link);
    if (link.href === RELOAD_PAGE) {
      this.refresh();
    } else {
      window.location.href = link.href;
    }
  }

  /**
   *  Navigates with window.open using '_blank'
   * @param link the ILink data descriptor containing the absolute URL href property
   */
  private linkWindowBlank(link: ILink): void {
    this.loggerService.log('[LinkService]::linkWindowBlank', link);
    this.settlePopup(window.open(link.href, '_blank'), link, '_blank');
  }

  /**
   * Settles a popup that has just been asked for: records the ones that never
   * opened, and cuts the ones that did loose from this window.
   *
   * `window.open` answers with `null` when the browser refuses the popup, which
   * is routine in the in-app browsers inside apps like Facebook, Instagram and
   * TikTok. Nothing else notices: the user taps, no window appears, and the
   * refusal otherwise leaves no trace anywhere. Reporting is all this does —
   * recovering needs a product decision, because every option (an inline link
   * to press again, a snackbar) needs copy.
   *
   * A popup that DOES open arrives holding `window.opener`: a live handle on
   * this widget that the destination can use to navigate it elsewhere while the
   * participant is reading the other tab. Clearing that handle is the narrow
   * fix. The usual one, passing `noopener`, would cost both of the things this
   * method depends on — some browsers drop a popup's size and position when it
   * is set, and it answers `null` whether or not the window opened, which would
   * take the check above down with it. The threat here is modest, since whoever
   * authors a link already decides where it points; this is worth doing because
   * it is free, not because it is urgent.
   *
   * Only the two genuine popups come through here. `_parent` and `_top` navigate
   * a frame rather than opening a window, so blockers do not apply to them, and
   * a widget embedded cross-origin cannot reliably read the handle back for
   * either — a `null` there would mean nothing.
   *
   * @param opened whatever `window.open` returned
   * @param link the link that was being opened
   * @param target which of the two popup routes this came from
   */
  private settlePopup(opened: Window | null, link: ILink, target: '_blank' | 'social'): void {
    if (!opened) {
      this.loggerService.warn(
        '[LinkService] - the browser refused to open this link; a popup blocker is the usual cause',
        link.href,
        target,
      );

      addBreadcrumb({
        category: 'navigation',
        level: 'warning',
        message: 'Popup blocked',
        data: { href: link.href, target },
      });

      return;
    }

    // `opener` is one of the few properties a cross-origin window still exposes,
    // so this reaches real destinations rather than only same-origin ones. It is
    // guarded all the same: a browser that refuses the write must not take the
    // navigation down with it, since the popup itself has already opened and the
    // participant is looking at it.
    try {
      opened.opener = null;
    } catch (error) {
      this.loggerService.warn('[LinkService] - could not clear the popup opener reference', link.href, error);
    }
  }

  /**
   * Navigates a frame above this one, surviving a browser that refuses.
   *
   * `window.open` hands back the target's `WindowProxy`, and when that target is a cross-origin
   * ancestor, the object it returns is one the browser will refuse most writes to. Injected page
   * scripts and browser extensions write to it anyway — overwriting `blur` on whatever `open`
   * returns is a stock anti-popunder technique — and the refusal surfaces as a `SecurityError`
   * thrown out of this call. Unguarded, that ends the participant's session on the fatal screen
   * over a write this app never made.
   *
   * Nothing is retried or substituted, because there is nothing to substitute: a browser that
   * will not move the frame will not move it for a different call either. The refusal is recorded
   * and the app is left standing, which is the better of the two available outcomes — the
   * participant keeps a working screen instead of losing their place in the experience.
   *
   * The catch cannot mask an ordinary fault, because the ordinary faults here do not throw: a
   * sandbox refusing top navigation logs to the console, and an unusable href simply navigates
   * nowhere. Only the exotic cases reach it.
   *
   * Reported rather than merely logged, under one fingerprint for the whole app, so this reads as
   * a single issue to archive against a threshold rather than one per campaign — and so a change
   * in how often it happens is still visible. `warning` because the app is intact afterwards and
   * the cause is outside it.
   *
   * @param link the ILink data descriptor containing the absolute URL href property
   * @param target which ancestor to navigate
   */
  private openAncestorFrame(link: ILink, target: '_parent' | '_top'): void {
    try {
      window.open(link.href, target);
    } catch (error) {
      this.loggerService.warn('[LinkService] - the browser refused to navigate this frame', link.href, target, error);

      captureException(error, {
        level: 'warning',
        fingerprint: ['link-frame-navigation-refused'],
        tags: { 'link.target': target },
      });
    }
  }

  /**
   * Navigates with window.open using '_parent'
   * @param link the ILink data descriptor containing the absolute URL href property
   */
  private linkWindowParent(link: ILink): void {
    this.loggerService.log('[LinkService]::linkWindowParent', link);
    this.openAncestorFrame(link, '_parent');
  }

  /**
   * Navigates with window.open using `_top`
   * @param link the ILink data descriptor containing the absolute URL href property
   */
  private linkWindowTop(link: ILink): void {
    this.loggerService.log('[LinkService]::linkWindowTop', link);
    this.openAncestorFrame(link, '_top');
  }

  /**
   * Navigates with the Social Sharer in a featured window
   * @param link the ILink data descriptor containing the absolute URL href property
   */
  private linkSocial(link: ILink): void {
    this.loggerService.log('[LinkService]::linkSocial', link);

    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;

    // The popup opens at the constant size, shrunk to fit anything smaller.
    // Height takes one extra step: on a screen shorter than the constant it
    // uses 80% of the height rather than all of it, so the browser's own
    // chrome still has somewhere to sit.
    const width = Math.min(SOCIAL_SHARING_FEATURES.WIDTH, screenWidth);
    const height = screenHeight < SOCIAL_SHARING_FEATURES.HEIGHT ? Math.floor(screenHeight * 0.8) : SOCIAL_SHARING_FEATURES.HEIGHT;

    // centered on the screen at that final size
    const left = Math.floor(screenWidth / 2 - width / 2);
    const top = Math.floor(screenHeight / 2 - height / 2);

    const featureString = [`width=${width}`, `height=${height}`, `left=${left}`, `top=${top}`].join(',');

    this.settlePopup(window.open(link.href, '_blank', featureString), link, 'social');
  }

  /**
   * Refreshes the whole Application
   */
  refresh(): void {
    window.location.reload();
  }
}
